import {useEffect} from 'react';

import {
  createDialogSessionToken,
  type DialogMessagePayload,
  inboundSyncDialogUrl,
  type IncomingSyncSelection,
  parseCreateIncomingSyncSelection,
  parseDialogMessage,
  type RoamingSettingsUpdate,
  stringifyDialogMessage,
} from './inboundSyncDialogProtocol';
import {
  readAllFileAttachmentContents,
  type ReadAttachmentContent,
  readMessageHtmlBody,
  type ReadMessageMetadata,
  readReadMessageMetadata,
} from './outlookReadItem';
import {createIncomingOutlookEmailSync} from './requests';
import * as Roaming from './roamingSettings';
import type {RoamingSettingsState} from './RoamingSettingsContext';
import {usePodRequest} from './usePodRequest';

type Options = {
  enabled: boolean;
  onClose: () => void;
  onOpenFailed: () => void;
};

type CapturedReadMessage = {
  metadata: ReadMessageMetadata;
  htmlBody: string | null;
  attachmentContents: Promise<ReadAttachmentContent[]>;
};

const readRoamingSettingsState = (): RoamingSettingsState => ({
  emailSyncOnSend: Roaming.getEmailSyncOnSend() ?? true,
  podUrl: Roaming.getPodUrl(),
  refreshToken: Roaming.getRefreshToken(),
  jwt: Roaming.getJwt(),
});

const applyRoamingSettingsUpdate = async (
  updates: RoamingSettingsUpdate
): Promise<RoamingSettingsState> => {
  if (updates.emailSyncOnSend !== undefined) {
    await Roaming.setEmailSyncOnSend(updates.emailSyncOnSend);
  }
  if (updates.podUrl !== undefined) {
    await Roaming.setPodUrl(updates.podUrl);
  }
  if (updates.refreshToken !== undefined) {
    await Roaming.setRefreshToken(updates.refreshToken);
  }
  if (updates.clearJwtAndRefreshTokens) {
    await Roaming.clearJwtAndRefreshTokens();
  }
  return readRoamingSettingsState();
};

export const useInboundSyncDialog = ({
  enabled,
  onClose,
  onOpenFailed,
}: Options): void => {
  const {ensureAccessToken, postRequest} = usePodRequest();

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (typeof Office === 'undefined' || !Office.context?.ui) {
      onOpenFailed();
      return;
    }

    const dialogSessionToken = createDialogSessionToken();
    const dialogUrl = inboundSyncDialogUrl(dialogSessionToken);
    let dialog: Office.Dialog | null = null;
    let cancelled = false;
    let closed = false;
    let capturedReadMessage: CapturedReadMessage | null = null;
    let captureReadMessagePromise: Promise<CapturedReadMessage | null> | null =
      null;

    const closeOfficeDialog = (dialogToClose = dialog) => {
      if (dialog === dialogToClose) {
        dialog = null;
      }
      if (dialogToClose) {
        try {
          dialogToClose.close();
        } catch {
          // Dialog may already be closed.
        }
      }
    };

    const messageChild = (payload: DialogMessagePayload) => {
      dialog?.messageChild(stringifyDialogMessage(payload));
    };

    const closeDialog = () => {
      if (closed) {
        return;
      }
      closed = true;
      try {
        closeOfficeDialog();
      } finally {
        onClose();
      }
    };

    const captureReadMessage = (): Promise<CapturedReadMessage | null> => {
      if (capturedReadMessage) {
        return Promise.resolve(capturedReadMessage);
      }
      if (captureReadMessagePromise) {
        return captureReadMessagePromise;
      }

      captureReadMessagePromise = (async () => {
        const [metadata, htmlBody] = await Promise.all([
          readReadMessageMetadata(),
          readMessageHtmlBody(),
        ]);
        if (!metadata) {
          return null;
        }

        // Capture attachment bytes immediately so a later mailbox selection
        // change cannot mix entity links from this message with another.
        const attachmentContents = readAllFileAttachmentContents(
          metadata.attachments
        );
        capturedReadMessage = {metadata, htmlBody, attachmentContents};
        return capturedReadMessage;
      })().finally(() => {
        captureReadMessagePromise = null;
      });

      return captureReadMessagePromise;
    };

    const sendRoamingSettings = () => {
      void ensureAccessToken()
        .catch(() => undefined)
        .then((accessToken) => {
          messageChild({
            type: 'roamingSettings',
            settings: {...readRoamingSettingsState(), jwt: accessToken},
          });
        });
    };

    const sendReadMessageMetadata = () => {
      void captureReadMessage().then((captured) => {
        if (!captured) {
          return;
        }
        messageChild({
          type: 'readMessageMetadata',
          metadata: captured.metadata,
        });
      });
    };

    const handleCreateIncomingSync = async (
      selection: IncomingSyncSelection
    ) => {
      try {
        const captured = await captureReadMessage();
        if (!captured) {
          messageChild({
            type: 'incomingSyncCreated',
            ok: false,
            error: 'Could not read the current message.',
          });
          return;
        }

        const {metadata, htmlBody} = captured;
        const attachmentContents = await captured.attachmentContents;

        const result = await createIncomingOutlookEmailSync(postRequest, {
          agencyId: selection.agencyId,
          subject: metadata.subject,
          fromAddress: metadata.fromAddress,
          internetMessageId: metadata.internetMessageId,
          microsoftMessageId: metadata.microsoftMessageId,
          microsoftConversationId: metadata.microsoftConversationId,
          inboxEmailAddress: metadata.inboxEmailAddress,
          sentAt: metadata.sentAt,
          htmlBody,
          personLinks: selection.personLinks,
          companyLinks: selection.companyLinks,
          jobLinks: selection.jobLinks,
          dealLinks: selection.dealLinks,
          recipients: metadata.recipients,
          attachments: metadata.attachments,
          attachmentContents,
        });

        messageChild({
          type: 'incomingSyncCreated',
          ok: result.ok,
          errors: result.errors,
        });
      } catch (error: unknown) {
        messageChild({
          type: 'incomingSyncCreated',
          ok: false,
          error:
            error instanceof Error
              ? error.message
              : 'Failed to save email to Loxo',
        });
      }
    };

    const isTrustedDialogMessage = (
      arg: {origin?: string},
      message: DialogMessagePayload
    ) => {
      if (arg.origin && arg.origin !== window.location.origin) {
        return false;
      }
      return message.parentDialogToken === dialogSessionToken;
    };

    Office.context.ui.displayDialogAsync(
      dialogUrl,
      {height: 75, width: 55, displayInIframe: false},
      (asyncResult) => {
        if (cancelled) {
          closeOfficeDialog(asyncResult.value ?? null);
          return;
        }

        if (asyncResult.status !== Office.AsyncResultStatus.Succeeded) {
          onOpenFailed();
          return;
        }

        dialog = asyncResult.value;

        dialog.addEventHandler(
          Office.EventType.DialogMessageReceived,
          (arg) => {
            if (!('message' in arg)) {
              return;
            }

            const message = parseDialogMessage(arg.message);
            if (!message) {
              return;
            }

            if (!isTrustedDialogMessage(arg, message)) {
              closeDialog();
              return;
            }

            if (message.type === 'ready') {
              sendRoamingSettings();
              sendReadMessageMetadata();
              return;
            }

            if (message.type === 'getReadMessageMetadata') {
              sendReadMessageMetadata();
              return;
            }

            if (message.type === 'getRoamingSettings') {
              sendRoamingSettings();
              return;
            }

            if (message.type === 'updateRoamingSettings') {
              void applyRoamingSettingsUpdate(
                (message.updates ?? {}) as RoamingSettingsUpdate
              )
                .then((settings) => {
                  messageChild({
                    type: 'roamingSettingsUpdated',
                    ok: true,
                    settings,
                  });
                })
                .catch((error: unknown) => {
                  messageChild({
                    type: 'roamingSettingsUpdated',
                    ok: false,
                    error:
                      error instanceof Error
                        ? error.message
                        : 'Failed to update roaming settings',
                  });
                });
              return;
            }

            const selection = parseCreateIncomingSyncSelection(message);
            if (selection) {
              void handleCreateIncomingSync(selection);
              return;
            }

            if (message.status === 'success' || message.status === 'cancel') {
              closeDialog();
            }
          }
        );

        dialog.addEventHandler(Office.EventType.DialogEventReceived, () => {
          closeDialog();
        });
      }
    );

    return () => {
      cancelled = true;
      closeOfficeDialog();
    };
  }, [enabled, ensureAccessToken, onClose, onOpenFailed, postRequest]);
};
